import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { firstScreenLocales } from '../../locales/firstScreen';
import { Footer } from '../UI/Footer/Footer';
import { useSwipeable } from 'react-swipeable';

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  min-width: 320px;
  min-height: 568px;
  box-sizing: border-box;
  overflow: auto;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: clamp(16px, 4vw, 24px);
  padding-bottom: 100px;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const GlobeButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 44px;
  height: 44px;
  background: #232323cc;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px #0004;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background 0.15s;
  &:hover {
    background: #0180ffcc;
  }
`;

const GlobeButtonInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LangLabel = styled.span`
  margin-top: 2px;
  font-size: 10px;
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-radius: 6px;
  padding: 0 8px;
  line-height: 18px;
  min-width: 28px;
  text-align: center;
  pointer-events: none;
  user-select: none;
`;

const LangMenu = styled.div`
  position: absolute;
  top: 62px;
  right: 18px;
  background: #232323;
  border-radius: 12px;
  box-shadow: 0 2px 12px #0007;
  padding: 11px 8px;
  min-width: 120px;
  z-index: 20;
  display: flex;
  flex-direction: column;
`;

const LangOption = styled.button<{ active?: boolean }>`
  background: ${({ active }) => (active ? '#0180ff' : 'transparent')};
  color: #fff;
  border: none;
  width: 100%;
  padding: 10px 18px;
  text-align: left;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover {
    background: #0180ff;
  }
`;

const MainCard = styled.div`
  position: relative;
  background-size: cover;
  border-radius: 20px;

  margin: clamp(10px, 3vh, 30px) 0 clamp(20px, 5vh, 40px);
  text-align: center;
  min-height: clamp(200px, 40vh, 400px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 auto;

  & > div:first-child {
    margin-top: clamp(20px, 5vh, 40px);
    align-self: flex-start;
    width: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #0180ff;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
  padding-bottom: 80px;
`;

const Title = styled.h1`
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 42px;
  font-weight: 600;
  margin-bottom: 11px;
`;

const GuideBadge = styled.div`
  width: 100%;
  max-width: 314px;
  height: 60px;
  margin: 0 auto 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const BadgeText = styled.p`
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
  text-align: center;
  text-shadow:
    0 0 5px rgba(255, 255, 255, 0.4),
    0 0 10px rgba(255, 255, 255, 0.3),
    0 0 15px rgba(255, 255, 255, 0.2),
    0 0 20px rgba(255, 255, 255, 0.15),
    0 0 25px rgba(255, 255, 255, 0.1);
  letter-spacing: 0.2px;
  max-width: 100%;
`;

const Description = styled.p`
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.01em;
  text-align: center;
  margin-bottom: 65px;
  white-space: pre-wrap;
  max-width: 314px;

  strong {
    font-weight: 700;
  }
`;

const AnimatedTitle = motion(Title);
const AnimatedGuideBadge = motion(GuideBadge);
const AnimatedDescription = motion(Description);

const textAnimation = {
  hidden: {
    y: 50,
    opacity: 0,
  },
  visible: (custom: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: custom * 0.2,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

const LANG_KEY = 'stonpools-lang';

const GlobeIcon = () => (
  <img
    src="/assets/globus.svg"
    alt="Globe"
    style={{
      width: 18,
      height: 18,
      display: 'block',
      filter: 'invert(1) brightness(1000%)', // делает иконку белой
      pointerEvents: 'none',
      userSelect: 'none',
    }}
    draggable={false}
  />
);

interface FirstScreenProps {
  onSwipeLeft?: () => void;
}

const FirstScreen: React.FC<FirstScreenProps> = ({ onSwipeLeft }) => {
  const [lang, setLang] = useState<'ru' | 'en'>(() => {
    const stored = localStorage.getItem(LANG_KEY) as 'ru' | 'en' | null;
    return stored || 'en';
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const t = firstScreenLocales[lang as 'ru' | 'en'];

  const descriptionContent = React.useMemo(() => {
    if (lang === 'en' && typeof t.description === 'string') {
      const [boldPartRaw, ...restParts] = t.description.split('\n\n');
      const boldPart = boldPartRaw?.trim() ?? '';
      const restText = restParts.join('\n\n');

      return (
        <>
          <strong>{boldPart}</strong>
          {restText ? `\n\n${restText}` : null}
        </>
      );
    }

    return t.description;
  }, [lang, t.description]);

  const startText = t.start;

  useEffect(() => {
    // Инициализация Telegram Web App
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      try {
        // Готовим Web App к использованию
        window.Telegram.WebApp.ready();

        // Расширяем Web App на весь экран
        window.Telegram.WebApp.expand();

        console.log('Telegram Web App инициализирован успешно');
        console.log(
          'Пользователь:',
          window.Telegram.WebApp.initDataUnsafe?.user
        );

        // Устанавливаем цветовую схему
        document.documentElement.setAttribute(
          'data-theme',
          window.Telegram.WebApp.colorScheme
        );

        // Применяем цвета темы
        if (window.Telegram.WebApp.themeParams) {
          const root = document.documentElement;
          const theme = window.Telegram.WebApp.themeParams;

          if (theme.bg_color) {
            root.style.setProperty('--tg-theme-bg-color', theme.bg_color);
          }
          if (theme.text_color) {
            root.style.setProperty('--tg-theme-text-color', theme.text_color);
          }
          if (theme.hint_color) {
            root.style.setProperty('--tg-theme-hint-color', theme.hint_color);
          }
          if (theme.link_color) {
            root.style.setProperty('--tg-theme-link-color', theme.link_color);
          }
          if (theme.button_color) {
            root.style.setProperty(
              '--tg-theme-button-color',
              theme.button_color
            );
          }
          if (theme.button_text_color) {
            root.style.setProperty(
              '--tg-theme-button-text-color',
              theme.button_text_color
            );
          }
        }
      } catch (error) {
        console.error('Ошибка при инициализации Telegram Web App:', error);
      }
    } else {
      console.log(
        'Telegram Web App API недоступен - запуск в обычном браузере'
      );
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
  }, [lang]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <div
      {...useSwipeable({
        onSwipedLeft: () => onSwipeLeft?.(),
        preventScrollOnSwipe: false,
        trackMouse: true,
        delta: 50,
        swipeDuration: 500,
      })}
    >
      <Container>
        {/* Языковая кнопка */}
        <GlobeButton
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Change language"
        >
          <GlobeButtonInner>
            <GlobeIcon />
            <LangLabel>{lang.toUpperCase()}</LangLabel>
          </GlobeButtonInner>
        </GlobeButton>
        {menuOpen && (
          <LangMenu ref={menuRef}>
            <LangOption
              active={lang === 'ru'}
              onClick={() => {
                setLang('ru');
                setMenuOpen(false);
              }}
            >
              Русский
            </LangOption>
            <LangOption
              active={lang === 'en'}
              onClick={() => {
                setLang('en');
                setMenuOpen(false);
              }}
            >
              English
            </LangOption>
          </LangMenu>
        )}
        <MainCard>
          <div>
            <AnimatedTitle
              variants={textAnimation}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              {t.title}
            </AnimatedTitle>
            <AnimatedGuideBadge
              variants={textAnimation}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <BadgeText>{t.badge1}</BadgeText>
              <BadgeText>{t.badge2}</BadgeText>
            </AnimatedGuideBadge>
          </div>
          <AnimatedDescription
            variants={textAnimation}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            {descriptionContent}
          </AnimatedDescription>
        </MainCard>
        <Footer textRight={startText} onSwipeLeft={onSwipeLeft} />
      </Container>
    </div>
  );
};

export default FirstScreen;
